import React from 'react';
import { IoStop } from 'react-icons/io5';
import { MdError, MdContentCopy, MdCheck, MdRefresh, MdEdit, MdLightbulbOutline, MdOutlineThumbUp, MdOutlineThumbDown, MdOutlineVolumeUp, MdPause } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import ReferenceCards from './ReferenceCards';
import { References } from '../types';

interface MessageCardProps {
  isBot: boolean;
  content?: string;
  stopped?: boolean;
  loading?: boolean;
  error?: boolean;
  references?: References;
  reask?: string[];
  messageId?: string;
  userFeedback?: 'like' | 'dislike' | null;
  onRetry?: () => void;
  onEdit?: () => void;
  onQuickQuestionClick?: (question: string) => void;
  onFeedback?: (messageId: string, feedback: 'like' | 'dislike' | null) => void;
}

// Strip markdown syntax so TTS reads clean plain text
const stripMarkdown = (text: string): string => {
  return text
    .replace(/```[\s\S]*?```/g, '')           // remove fenced code blocks entirely
    .replace(/`[^`]+`/g, '')                   // remove inline code
    .replace(/!\[.*?\]\(.*?\)/g, '')           // remove images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // links → link text only
    .replace(/#{1,6}\s+/g, '')                 // remove heading markers
    .replace(/(\*\*|__)(.*?)\1/g, '$2')        // bold
    .replace(/(\*|_)(.*?)\1/g, '$2')           // italic
    .replace(/~~(.*?)~~/g, '$1')               // strikethrough
    .replace(/^\s*[-*+]\s+/gm, '')             // unordered list bullets
    .replace(/^\s*\d+\.\s+/gm, '')             // ordered list numbers
    .replace(/^\s*>{1,}\s?/gm, '')             // blockquotes
    .replace(/\|.*\|/g, '')                    // table rows
    .replace(/[-|]{3,}/g, '')                  // table separators
    .replace(/\n{3,}/g, '\n\n')               // collapse excess blank lines
    .trim();
};

const MessageCard: React.FC<MessageCardProps> = ({
  isBot,
  content,
  stopped,
  loading,
  error,
  references,
  reask,
  messageId,
  userFeedback,
  onRetry,
  onEdit,
  onQuickQuestionClick,
  onFeedback,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [localFeedback, setLocalFeedback] = React.useState<'like' | 'dislike' | null>(userFeedback ?? null);
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);

  React.useEffect(() => {
    setLocalFeedback(userFeedback ?? null);
  }, [userFeedback]);

  // Cancel any ongoing speech when the component unmounts
  React.useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const handleCopy = async () => {
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const handleFeedback = (type: 'like' | 'dislike') => {
    if (!messageId || !onFeedback) return;
    const next = localFeedback === type ? null : type;
    setLocalFeedback(next);
    onFeedback(messageId, next);
  };

  const handleReadAloud = () => {
    if (!content) return;
    const synth = window.speechSynthesis;

    if (isSpeaking) {
      if (isPaused) {
        synth.resume();
        setIsPaused(false);
      } else {
        synth.pause();
        setIsPaused(true);
      }
      return;
    }

    // Start a new utterance
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(stripMarkdown(content));
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    utterance.onstart = () => { setIsSpeaking(true); setIsPaused(false); };
    utterance.onend   = () => { setIsSpeaking(false); setIsPaused(false); };
    utterance.onerror = () => { setIsSpeaking(false); setIsPaused(false); };

    synth.speak(utterance);
  };

  // Custom components for markdown rendering
  const components: Components = {
    code({ children, className, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const isInline = !match;
      return isInline ? (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>{children}</code>
      ) : (
        <code className={`${className} block bg-gray-100 p-2 rounded text-sm overflow-x-auto`} {...props}>{children}</code>
      );
    },
    a({ children, ...props }) {
      return (
        <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          {children}
        </a>
      );
    }
  };

  // Read-aloud button icon & tooltip
  const ReadAloudIcon = isSpeaking && !isPaused ? MdPause : MdOutlineVolumeUp;
  const readAloudTitle = !isSpeaking ? 'Read aloud' : isPaused ? 'Resume' : 'Pause';

  return (
    <div className="relative group">
      <div
        className={`rounded-xl p-4 min-w-0 relative ${
          isBot ? 'bg-white border border-gray-200' : 'bg-blue-600 text-white'
        }`}
      >
        <div className="text-sm break-words">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-300"></div>
            </div>
          ) : isBot ? (
            <div className="prose prose-sm max-w-none prose-headings:mt-3 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-blockquote:border-l-blue-500">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {content || ''}
              </ReactMarkdown>
            </div>
          ) : (
            <span className="whitespace-pre-wrap">{content}</span>
          )}

          {!loading && stopped && (
            <div className={`flex items-center gap-1 mt-2 ${isBot ? 'text-red-500' : 'text-red-200'}`}>
              <IoStop className="w-4 h-4" />
              <span className="text-xs">Response stopped</span>
            </div>
          )}
          {!loading && error && (
            <div className={`flex items-center gap-1 mt-2 ${isBot ? 'text-red-500' : 'text-red-200'}`}>
              <MdError className="w-4 h-4" />
              <span className="text-xs">Sorry, there was an error processing your request. Please try it again.</span>
            </div>
          )}
        </div>

        {isBot && !loading && !error && references && <ReferenceCards references={references} />}

        {isBot && !loading && !error && reask && reask.length > 0 && (
          <div className="mt-4 border border-orange-200 rounded-lg p-4 bg-orange-50">
            <div className="flex items-center gap-2 mb-3">
              <MdLightbulbOutline className="w-5 h-5 text-orange-600" />
              <h4 className="text-sm font-semibold text-orange-900">Suggested SDG Questions</h4>
            </div>
            <div className="space-y-2">
              {reask.map((question, index) => {
                const key = `${question}-${index}`;
                const isClickable = Boolean(onQuickQuestionClick);
                const base = 'w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors';
                const cls = isClickable
                  ? 'bg-white border-orange-200 text-orange-700 hover:bg-orange-50'
                  : 'bg-white border-orange-100 text-orange-500 cursor-default';
                return isClickable ? (
                  <button type="button" key={key} className={`${base} ${cls}`} onClick={() => onQuickQuestionClick?.(question)}>
                    {question}
                  </button>
                ) : (
                  <div key={key} className={`${base} ${cls}`}>{question}</div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action buttons — appear at bottom of card on hover */}
        {!loading && (
          <>
            {isBot && content && (
              <div className="absolute bottom-0 left-6 transform translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2">
                {!stopped && !error && (
                  <>
                    {/* Copy */}
                    <button
                      onClick={handleCopy}
                      className="flex items-center justify-center w-7 h-7 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 rounded-full shadow-sm transition-colors"
                      title={copied ? 'Copied!' : 'Copy response'}
                    >
                      {copied ? <MdCheck className="w-4 h-4" /> : <MdContentCopy className="w-4 h-4" />}
                    </button>

                    {/* Read Aloud — blue when active */}
                    <button
                      onClick={handleReadAloud}
                      className={`flex items-center justify-center w-7 h-7 rounded-full border shadow-sm transition-colors ${
                        isSpeaking
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600'
                      }`}
                      title={readAloudTitle}
                    >
                      <ReadAloudIcon className="w-4 h-4" />
                    </button>
                  </>
                )}

                {/* Retry */}
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="flex items-center justify-center w-7 h-7 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 rounded-full shadow-sm transition-colors"
                    title="Retry response"
                  >
                    <MdRefresh className="w-4 h-4" />
                  </button>
                )}

                {/* Like / Dislike */}
                {messageId && onFeedback && !stopped && !error && (
                  <>
                    <button
                      onClick={() => handleFeedback('like')}
                      className={`flex items-center justify-center w-7 h-7 rounded-full border shadow-sm transition-colors ${
                        localFeedback === 'like'
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-green-50 hover:border-green-400 hover:text-green-600'
                      }`}
                      title={localFeedback === 'like' ? 'Remove like' : 'Like this response'}
                    >
                      <MdOutlineThumbUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleFeedback('dislike')}
                      className={`flex items-center justify-center w-7 h-7 rounded-full border shadow-sm transition-colors ${
                        localFeedback === 'dislike'
                          ? 'bg-red-500 border-red-500 text-white'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-red-50 hover:border-red-400 hover:text-red-500'
                      }`}
                      title={localFeedback === 'dislike' ? 'Remove dislike' : 'Dislike this response'}
                    >
                      <MdOutlineThumbDown className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            )}

            {!isBot && onEdit && (
              <div className="absolute bottom-0 right-6 transform translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={onEdit}
                  className="flex items-center justify-center w-7 h-7 bg-blue-600 border border-white text-white hover:bg-blue-500 hover:border-blue-400 rounded-full shadow-sm transition-colors"
                  title="Edit question"
                >
                  <MdEdit className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MessageCard;